import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Users, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { 
  Doctor, 
  Staff, 
  getDoctorPreferences, 
  updateDoctorPreferences,
  DoctorStaffPreferenceWithDetails 
} from '@/services/adminService';

interface DoctorPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctor: Doctor;
  staff: Staff[];
  onPreferencesUpdated?: () => void;
}

interface PreferenceSlot {
  rank: 1 | 2 | 3 | 4;
  staffId: string | null;
  label: string;
  description: string;
}

const DoctorPreferencesModal = ({
  isOpen,
  onClose,
  doctor,
  staff,
  onPreferencesUpdated
}: DoctorPreferencesModalProps) => {
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<PreferenceSlot[]>([
    { rank: 1, staffId: null, label: 'Main Nurse', description: 'Primary dental assistant' },
    { rank: 2, staffId: null, label: 'Primary Backup', description: 'First backup option' },
    { rank: 3, staffId: null, label: 'Secondary Backup', description: 'Second backup option' },
    { rank: 4, staffId: null, label: 'Third Backup', description: 'Final fallback option' }
  ]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Filter staff to show all active staff members
  const dentalAssistants = staff.filter(s => 
    s.status === 'active'
  );

  // Load existing preferences
  useEffect(() => {
    if (isOpen && doctor.id) {
      loadPreferences();
    }
  }, [isOpen, doctor.id]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const existingPreferences = await getDoctorPreferences(doctor.id);
      
      setPreferences(prev => prev.map(slot => {
        const existing = existingPreferences.find(p => p.preference_rank === slot.rank);
        return {
          ...slot,
          staffId: existing?.staff_id || null
        };
      }));
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast({
        title: "Error",
        description: "Failed to load doctor preferences",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStaffSelection = (rank: 1 | 2 | 3 | 4, staffId: string | null) => {
    setPreferences(prev => prev.map(pref => 
      pref.rank === rank ? { ...pref, staffId } : pref
    ));
  };

  const removeStaffFromSlot = (rank: 1 | 2 | 3 | 4) => {
    handleStaffSelection(rank, null);
  };

  const getAvailableStaff = (currentRank: 1 | 2 | 3 | 4) => {
    const usedStaffIds = preferences
      .filter(p => p.rank !== currentRank && p.staffId)
      .map(p => p.staffId);
    
    return dentalAssistants.filter(s => !usedStaffIds.includes(s.id));
  };

  const validatePreferences = () => {
    // Check for duplicate staff assignments
    const staffIds = preferences.filter(p => p.staffId).map(p => p.staffId);
    const uniqueStaffIds = new Set(staffIds);
    
    if (staffIds.length !== uniqueStaffIds.size) {
      toast({
        title: "Validation Error",
        description: "Each staff member can only be assigned to one preference rank",
        variant: "destructive"
      });
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validatePreferences()) {
      return;
    }

    setSaving(true);
    try {
      const preferencesToSave = preferences
        .filter(p => p.staffId)
        .map(p => ({
          staff_id: p.staffId!,
          preference_rank: p.rank
        }));

      await updateDoctorPreferences(doctor.id, preferencesToSave);

      toast({
        title: "Success",
        description: "Doctor preferences updated successfully",
      });

      onPreferencesUpdated?.();
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Error",
        description: "Failed to save doctor preferences",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getStaffDisplayName = (staffId: string) => {
    const staffMember = dentalAssistants.find(s => s.id === staffId);
    return staffMember ? (staffMember.short_name || staffMember.name) : 'Unknown';
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1: return 'bg-blue-100 text-blue-800 border-blue-200';
      case 2: return 'bg-green-100 text-green-800 border-green-200';
      case 3: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 4: return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Staff Preferences - Dr {doctor.short_name || doctor.name}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading preferences...</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">4-Tier Priority System</p>
                <p>Set up to 4 dental assistants in order of preference. The system will automatically assign the highest available preference when scheduling.</p>
              </div>
            </div>

            {/* Preference Slots */}
            <div className="grid gap-4">
              {preferences.map((pref) => (
                <Card key={pref.rank} className="relative">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className={getRankColor(pref.rank)}>
                          #{pref.rank}
                        </Badge>
                        <span className="text-sm font-medium">{pref.label}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{pref.description}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-2">
                      {pref.staffId ? (
                        <div className="flex items-center space-x-2 flex-1">
                          <div className="flex-1 px-3 py-2 bg-muted rounded-md text-sm">
                            {getStaffDisplayName(pref.staffId)}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeStaffFromSlot(pref.rank)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Select
                          value=""
                          onValueChange={(value) => handleStaffSelection(pref.rank, value)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select dental assistant..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailableStaff(pref.rank).map((staffMember) => (
                              <SelectItem key={staffMember.id} value={staffMember.id}>
                                <div className="flex items-center justify-between w-full">
                                  <span>{staffMember.short_name || staffMember.name}</span>
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    {staffMember.role}
                                  </Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Summary */}
            <div className="pt-4 border-t">
              <div className="text-sm text-muted-foreground mb-2">
                Current assignments: {preferences.filter(p => p.staffId).length} of 4 preferences set
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DoctorPreferencesModal;